import React, { useRef } from 'react';
import { Heart, Users, Globe, Palette } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { navigate } from '../utils/navigation';
import HomeRegistrationForm from './HomeRegistrationForm';
import AdSense from './AdSense';

const WelcomeSection: React.FC = () => {
  const registrationFormRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  
  const features = [
    {
      icon: <Palette className="h-8 w-8 text-dpn-pink" />,
      title: 'Art & Craftsmanship',
      description: 'Celebrating the incredible artistry and traditional craftsmanship behind every celebration'
    },
    {
      icon: <Users className="h-8 w-8 text-dpn-light-green" />,
      title: 'Committee Spirit',
      description: 'Bringing together diverse communities through shared cultural experiences and celebrations'
    },
    {
      icon: <Globe className="h-8 w-8 text-dpn-olive" />,
      title: 'Global Heritage',
      description: 'Building a worldwide archive of cultural traditions and connecting celebrations across continents'
    },
    {
      icon: <Heart className="h-8 w-8 text-dpn-raspberry" />,
      title: 'Cultural Preservation',
      description: 'Preserving and promoting intangible cultural heritage for future generations'
    }
  ];

  return (
    <section id="welcome" className="bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="font-runtime text-3xl md:text-4xl font-bold text-black mb-6">
            More Than a Festival — A Living Cultural Archive
          </h2>
          <p className="font-lucy-rose text-lg md:text-xl text-gray-700 leading-relaxed">
            Durga Puja is more than a festival — it's art, culture, cuisine, and community coming together. 
            At Durga Puja Network, we're building a global cultural archive to help you connect, share, and celebrate wherever you are. 
            Our platform celebrates the creative force, storytelling traditions, and vibrant community spirit that makes 
            this UNESCO-recognized cultural heritage truly special.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="text-center p-6 rounded-xl bg-white shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="flex justify-center mb-4">
                {feature.icon}
              </div>
              <h3 className="font-runtime text-xl font-bold text-black mb-3">
                {feature.title}
              </h3>
              <p className="font-lucy-rose text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {!user && (
        <>
          {/* Advertisement Section */}
          <section className="py-8 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-center">
                <AdSense 
                  adSlot="1234567897" 
                  adFormat="horizontal"
                  className="w-full max-w-2xl"
                  adStyle={{ display: 'block', width: '728px', height: '90px' }}
                />
              </div>
            </div>
          </section>

          {/* Registration Form - Full Viewport Background */}
          <div className="min-h-screen w-full bg-[#cc2861] flex items-center justify-center p-4 sm:p-6 lg:p-8">
            <a id="registration-form" className="block" style={{ scrollMarginTop: '120px' }} />
            <div 
              ref={registrationFormRef}
              className="w-[95%] sm:w-[85%] md:w-[55%] lg:w-[40%] xl:w-[35%] bg-white rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-8 sm:p-10 lg:p-12 transform transition-all duration-300 hover:shadow-[0_8px_40px_rgb(0,0,0,0.16)]" 
            >
              <HomeRegistrationForm />
            </div>
          </div>
          
          {/* Call to Action - Below Registration Form */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 pb-10">
            <div className="bg-gradient-to-r from-dpn-pink to-dpn-raspberry p-8 rounded-2xl text-white text-center">
              <h3 className="font-runtime text-2xl font-bold mb-4">
                Join Our Cultural Movement
              </h3>
              <p className="font-lucy-rose text-lg opacity-95 mb-6">
                Be part of a global community that celebrates, preserves, and shares the rich cultural heritage of Durga Puja. 
                Together, we're creating something beautiful that transcends borders and brings people together.
              </p>
              <div className="flex justify-center">
                <button 
                  onClick={() => navigate('/signup')}
                  className="bg-white text-dpn-raspberry hover:bg-gray-100 font-lucy-rose font-medium px-8 py-3 rounded-lg transition-colors"
                >
                  Join the Global Network
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default WelcomeSection;